// Rollenwechsel zum Testen auf Staging (DCP-2228 Folge-Feature)
//
// Anders als der RoleSwitcher daneben: der ist an den MockAuthProvider gebunden und
// gibt bei echtem Entra-Login `null` zurück. Dieser hier arbeitet MIT einem echten
// Login — der Server stellt ein signiertes Token aus, das die angenommene Rolle an
// den angemeldeten Benutzer bindet.
//
// Erscheint nur, wenn `canSwitchRole` am Benutzer gesetzt ist UND die Umgebung es
// erlaubt. Beides prüft der Server; `me.canSwitchRole` fasst es für die Anzeige
// zusammen. Admin ist nicht wählbar — wer das könnte, könnte sich alles geben.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserCog } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useMe } from '@/hooks/usePermissions';
import { useDepots } from '@/services/event-service';
import { useSwitchRole } from '@/services/user-service';
import { apiClient } from '@/lib/api-client';
import { Role } from '@/types';
import { formatDepot } from '@/features/events/depot-format';

/**
 * Roles a tester may assume — mirrors SWITCHABLE_ROLES in the backend, which is the
 * authority. Admin is excluded by design (nobody switches themselves into full access).
 *
 * Mitarbeiter joined with PRD-021: the role now inherits the responsible partner's
 * contract window instead of resolving to nothing. ⚠️ Keep in step with the backend list —
 * an entry here that the backend rejects produces a 403 the tester cannot explain.
 */
export const SWITCHABLE: readonly Role[] = [Role.Zentral, Role.Partner, Role.Mitarbeiter];

export function StagingRoleSwitcher() {
  const { t } = useTranslation('users');
  const { toast } = useToast();

  const { data: me } = useMe();
  const { data: depots = [] } = useDepots();
  const switchMutation = useSwitchRole();

  const [open, setOpen] = useState(false);
  const [role, setRole] = useState<Role>(Role.Zentral);
  const [depotKey, setDepotKey] = useState('');

  // The single source for whether this is available: the server already combined the
  // per-user right with the environment flag. Also shown while a role is currently
  // assumed (22.09.2026-Follow-up), even though `canSwitchRole` itself is correctly
  // `false` then (it reflects the ASSUMED role now, e.g. a partner may not switch
  // roles — see get-me.ts) — otherwise the whole control vanishes the moment someone
  // switches, and the only way back was logging out completely.
  const isAssumed = me?.roleSource === 'assumed';
  if (!me?.canSwitchRole && !isAssumed) return null;

  const needsDepot = role === Role.Partner || role === Role.Mitarbeiter;

  const handleSwitch = () => {
    switchMutation.mutate(
      { role, depots: needsDepot && depotKey ? [depotKey] : [] },
      {
        onSuccess: () => {
          setOpen(false);
          // Full reload: the assumed role changes what nearly every screen may show,
          // and remounting from scratch is more trustworthy than hoping every cache
          // and piece of component state notices.
          window.location.reload();
        },
        onError: (e) =>
          toast({
            title: t('switch.failed'),
            description: e instanceof Error ? e.message : String(e),
            variant: 'destructive',
          }),
      }
    );
  };

  // No server round-trip needed (22.09.2026-Follow-up): the assumed role lives
  // entirely in this client-held token (see api-client.ts's setAssumedRoleToken
  // doc comment) — dropping it just lets every later request fall back to
  // whatever the real Entra token already grants, exactly like it did before
  // the first switch.
  const handleRevert = () => {
    apiClient.setAssumedRoleToken(undefined);
    window.location.reload();
  };

  return (
    <>
      <Button
        variant={isAssumed ? 'default' : 'outline'}
        size="sm"
        className="h-8 gap-2 text-xs"
        onClick={() => setOpen(true)}
        title={t('switch.tooltip')}
      >
        <UserCog className="h-4 w-4" />
        {isAssumed
          ? t('switch.active', { role: t(`switchRole.${me.role}`, me.role) })
          : t('switch.open')}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('switch.title')}</DialogTitle>
            <DialogDescription>
              {isAssumed ? t('switch.descriptionAssumed') : t('switch.description')}
            </DialogDescription>
          </DialogHeader>

          {isAssumed && (
            <Button variant="outline" className="gap-2" onClick={handleRevert}>
              <UserCog className="h-4 w-4" />
              {t('switch.revert')}
            </Button>
          )}

          <div className="space-y-4">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">
                {t('switch.role')}
              </label>
              <Select value={role} onValueChange={(v) => setRole(v as Role)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SWITCHABLE.map((r) => (
                    <SelectItem key={r} value={r}>
                      {t(`switchRole.${r}`, r)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {needsDepot && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">
                  {t('switch.depot')}
                </label>
                <Select value={depotKey} onValueChange={setDepotKey}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('switch.depotPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {depots.map((depot) => (
                      <SelectItem key={depot.key} value={depot.key}>
                        {formatDepot(depot)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <p className="text-xs text-muted-foreground">{t('switch.hint')}</p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t('form.cancel')}
            </Button>
            <Button
              onClick={handleSwitch}
              // A depot role without a depot would show an empty list and look broken;
              // the server rejects it too.
              disabled={switchMutation.isPending || (needsDepot && !depotKey)}
            >
              {switchMutation.isPending ? t('switch.switching') : t('switch.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
